import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  TrendingUp,
  Settings,
  History,
  Users,
  LogOut,
  Menu,
  X,
  LineChart,
  Building2,
  User,
  Bell,
  ChevronDown,
  Activity,
  Repeat,
  Home,
  BarChart3,
  Wallet,
  Shield,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCoinData } from '../contexts/CoinDataContext';
import { useAppSettings } from '../contexts/AppSettingsContext';
import { cn } from '../utils/cn';

const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const { coinData } = useCoinData();
  const location = useLocation();
  const navigate = useNavigate();

  const navigationGroups = [
    {
      title: 'Main',
      items: [
        // { name: 'Home', href: '/dashboard', icon: Home },
        { name: 'Arbitrage', href: '/arbitrage', icon: TrendingUp },
        { name: 'Spam Sell', href: '/spam-sell', icon: Activity },
        { name: 'Spam Sell (Worker)', href: '/spam-sell-worker', icon: Repeat },
      ]
    },
    // {
    //   title: 'Trading',
    //   items: [
    //     { name: 'Trading', href: '/trade', icon: LineChart },
    //     { name: 'History', href: '/history', icon: History },
    //   ]
    // },
    {
      title: 'Configuration',
      items: [
        { name: 'Exchanges', href: '/settings/exchanges', icon: Building2 },
        { name: 'Settings', href: '/settings', icon: Settings },
      ]
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="h-screen bg-black flex overflow-hidden">
      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 border-r border-gray-800 transform transition-transform duration-300 flex flex-col',
          sidebarOpen
            ? 'translate-x-0 lg:translate-x-0 lg:static lg:inset-0'
            : '-translate-x-full lg:-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800 flex-shrink-0">
          <h1 className="text-xl font-bold text-white">Arbitro</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 px-3 flex-1 overflow-y-auto space-y-6">
          {navigationGroups.map((group) => (
            <div key={group.title}>
              <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive(item.href)
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    )}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex-shrink-0 border-t border-gray-800">
          <div className="p-4">
            <div className="mb-4 px-3">
              <p className="text-xs text-gray-500">USDT/IDR</p>
              <p className="text-lg font-bold text-white">
                {coinData ? `${coinData.bid?.toLocaleString('id-ID') || 'N/A'} IDR` : 'Loading...'}
              </p>
            </div>
            <button
              onClick={logout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-6 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex items-center space-x-6">
          </div>

          <div className="flex items-center space-x-4">
            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center space-x-3 text-sm"
              >
                <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5" />
                </div>
                <span className="text-white">{user?.name || 'User'}</span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>

              {profileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-800 rounded-md shadow-lg py-1 z-10">
                  <Link
                    to="/settings/profile"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
                    onClick={() => setProfileDropdownOpen(false)}
                  >
                    Profile Settings
                  </Link>
                  <button
                    onClick={logout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-black">
          <div className="container mx-auto px-6 py-8">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default MainLayout;
